'use client'

import { memo, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

type NinxlDotWaveProps = {
  className?: string
}

/** baby: Full-bleed dotted wave field: canvas, no Three.js. */
export const NinxlDotWave = memo(function NinxlDotWave({ className }: NinxlDotWaveProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const frameRef = useRef(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined

    const ctx = canvas.getContext('2d')
    if (!ctx) return undefined

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let animationId = 0

    const SEPARATION = 26

    const resize = () => {
      const parent = canvas.parentElement
      if (!parent) return
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const { width, height } = parent.getBoundingClientRect()
      canvas.width = Math.floor(width * dpr)
      canvas.height = Math.floor(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const readDotRgb = (): string => {
      const root = canvas.closest('.ninxl-inkflow-showcase') ?? canvas.parentElement
      const style = getComputedStyle(root ?? canvas)
      return style.getPropertyValue('--ninxl-inkflow-dot-rgb').trim() || '33, 114, 141'
    }

    const draw = (count: number) => {
      const { width, height } = canvas.getBoundingClientRect()
      ctx.clearRect(0, 0, width, height)

      const dotRgb = readDotRgb()
      const amountX = Math.ceil(width / SEPARATION) + 6
      const amountY = Math.ceil(height / (SEPARATION * 0.38)) + 4
      const offsetX = (amountX * SEPARATION) / 2
      const offsetY = (amountY * SEPARATION * 0.38) / 2
      const centerY = height * 0.54

      for (let ix = 0; ix < amountX; ix += 1) {
        for (let iy = 0; iy < amountY; iy += 1) {
          const x = ix * SEPARATION - offsetX + width / 2
          const wave =
            Math.sin((ix + count) * 0.26) * 28 +
            Math.sin((iy + count * 0.65) * 0.38) * 18 +
            Math.sin((ix + iy + count * 0.4) * 0.14) * 10
          const y = centerY + iy * SEPARATION * 0.38 - offsetY + wave
          const depth = 0.35 + (iy / amountY) * 0.55
          const alpha = (0.12 + ((wave + 36) / 72) * 0.38) * depth
          const radius = 1 + depth * 0.9 + Math.max(0, wave / 40) * 0.4

          ctx.beginPath()
          ctx.fillStyle = `rgba(${dotRgb}, ${Math.min(alpha, 0.72).toFixed(3)})`
          ctx.arc(x, y, radius, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }

    resize()
    draw(0)

    const animate = () => {
      frameRef.current += reduceMotion ? 0 : 0.055
      draw(frameRef.current)
      animationId = window.requestAnimationFrame(animate)
    }

    animate()

    const observer = new ResizeObserver(resize)
    observer.observe(canvas.parentElement ?? canvas)
    window.addEventListener('resize', resize)

    return () => {
      window.cancelAnimationFrame(animationId)
      observer.disconnect()
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className={cn('ninxl-inkflow-dot-wave pointer-events-none absolute inset-0 h-full w-full', className)}
      aria-hidden
    />
  )
})
